import type { CrossZoneAlertEntry } from '../store/alertStore';
import { puToVolts } from './electricalConstants';
import { formatElementInfo } from './elementTypeLabels';
import { parseUtcTimestampMs } from './timeUtils';
import { fmt } from './numberFormat';

/**
 * Wording and colours of a cross-zone alert entry — shared by the notification center (bell)
 * and the Netzmonitoring page so one alert reads the same everywhere.
 */

/** Badge classes of an alert state (BLACK = overload, RED = endangered / band violated, YELLOW = warning). */
export function alertStateBadgeClass(newState: string): string {
  switch (newState) {
    case 'BLACK': return 'bg-gray-900 text-white dark:ring-1 dark:ring-gray-400';
    case 'RED': return 'bg-red-600 text-white';
    case 'YELLOW': return 'bg-yellow-500 text-white';
    default: return 'bg-green-500 text-white';
  }
}

export function alertTypeLabel(entry: Pick<CrossZoneAlertEntry, 'type'>): string {
  if (entry.type === 'trafo_alert') return 'Trafo';
  if (entry.type === 'cable_alert') return 'Kabel';
  return 'Spannung';
}

export function alertDetailText(entry: CrossZoneAlertEntry): string {
  if (entry.type === 'trafo_alert' || entry.type === 'cable_alert') {
    const kind = entry.type === 'trafo_alert' ? 'Trafo' : 'Kabel';
    const threshold = entry.thresholdValue != null ? ` (>${fmt(entry.thresholdValue, 0)}%)` : '';
    return `${kind}: ${entry.elementId ?? '?'} bei ${fmt(entry.metricValue, 1)}%${threshold}`;
  }
  const phaseLabel = entry.phase ? ` ${entry.phase}` : '';
  const elementInfo = formatElementInfo(entry.elementType, entry.elementName);
  const elementSuffix = elementInfo ? ` — ${elementInfo}` : '';
  return `Spannung${phaseLabel}: ${fmt(entry.metricValue, 3)} pu (${fmt(puToVolts(entry.metricValue), 1)}V)${elementSuffix}`;
}

/** "gerade eben" · "vor 12 Min." · "vor 3 Std." · "vor 2 Tagen" */
export function relativeTimeText(timestamp: string, nowMs: number = Date.now()): string {
  const minutes = Math.floor((nowMs - parseUtcTimestampMs(timestamp)) / 60000);
  if (minutes < 1) return 'gerade eben';
  if (minutes < 60) return `vor ${minutes} Min.`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `vor ${hours} Std.`;
  const days = Math.floor(hours / 24);
  return `vor ${days} Tag${days > 1 ? 'en' : ''}`;
}
